/**
 * 传奇殿堂页面
 *
 * 展示社区最高荣耀：
 * - 编辑精选
 * - 热门排行榜
 * - 顶尖骑士
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LegendCard, LegendCardSkeleton } from '../components/legend';
import * as legendApi from '../services/legendApi';
import styles from './HallOfFamePage.module.css';

export default function HallOfFamePage() {
    const { language } = useLanguage();

    const [featured, setFeatured] = useState([]);
    const [topLegends, setTopLegends] = useState([]);
    const [topRiders, setTopRiders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('week'); // week | month | all

    useEffect(() => {
        loadHallOfFame();
    }, []);

    useEffect(() => {
        loadLeaderboard();
    }, [period]);

    const loadHallOfFame = async () => {
        setLoading(true);
        try {
            const [featuredRes, hallRes] = await Promise.all([
                legendApi.getFeaturedLegends({ limit: 5 }),
                legendApi.getHallOfFame()
            ]);

            if (featuredRes.success) {
                setFeatured(featuredRes.data || []);
            }
            if (hallRes.success) {
                setTopRiders(hallRes.data?.topRiders || []);
            }
        } catch (err) {
            console.error('Failed to load hall of fame:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadLeaderboard = async () => {
        try {
            const result = await legendApi.getLeaderboard({ type: 'legends', period });
            if (result.success) {
                setTopLegends(result.data || []);
            }
        } catch (err) {
            console.error('Failed to load leaderboard:', err);
        }
    };

    const getRankBadge = (rank) => {
        switch (rank) {
            case 1: return { icon: '🥇', class: styles.gold };
            case 2: return { icon: '🥈', class: styles.silver };
            case 3: return { icon: '🥉', class: styles.bronze };
            default: return { icon: rank.toString(), class: styles.normal };
        }
    };

    const getLevelInfo = (level) => {
        return legendApi.getRiderLevelInfo(level || 1, language);
    };

    return (
        <div className={styles.page}>
            {/* 页面头部 */}
            <header className={styles.header}>
                <div className={styles.headerBg}></div>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>
                        <span className={styles.titleIcon}></span>
                        传奇殿堂
                    </h1>
                    <p className={styles.subtitle}>
                        记录每一位骑士的不朽荣耀
                    </p>
                </div>
            </header>

            {/* 编辑精选 */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <span className={styles.sectionIcon}></span>
                        编辑精选
                    </h2>
                    <p className={styles.sectionDesc}>由官方精选的卓越作品</p>
                </div>
                <div className={styles.featuredGrid}>
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <LegendCardSkeleton key={i} variant="featured" />
                        ))
                    ) : featured.length === 0 ? (
                        <div className={styles.emptyFeatured}>
                            <p>暂无编辑精选，敬请期待</p>
                        </div>
                    ) : (
                        featured.map((legend, index) => (
                            <LegendCard
                                key={legend.id}
                                legend={{ ...legend, is_featured: true }}
                                variant={index === 0 ? 'featured' : 'default'}
                            />
                        ))
                    )}
                </div>
            </section>

            {/* 排行榜 */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <span className={styles.sectionIcon}></span>
                        荣耀排行榜
                    </h2>
                    <div className={styles.periodTabs}>
                        <button
                            className={`${styles.periodTab} ${period === 'week' ? styles.active : ''}`}
                            onClick={() => setPeriod('week')}
                        >
                            本周
                        </button>
                        <button
                            className={`${styles.periodTab} ${period === 'month' ? styles.active : ''}`}
                            onClick={() => setPeriod('month')}
                        >
                            本月
                        </button>
                        <button
                            className={`${styles.periodTab} ${period === 'all' ? styles.active : ''}`}
                            onClick={() => setPeriod('all')}
                        >
                            总榜
                        </button>
                    </div>
                </div>

                <div className={styles.leaderboardGrid}>
                    {/* 传奇作品排行 */}
                    <div className={styles.leaderboardCard}>
                        <h3 className={styles.leaderboardTitle}>
                            <span></span> 热门传奇
                        </h3>
                        <div className={styles.leaderboardList}>
                            {topLegends.length === 0 ? (
                                <div className={styles.emptyList}>
                                    <p>暂无排行数据</p>
                                </div>
                            ) : (
                                topLegends.slice(0, 10).map((legend, index) => {
                                    const rankBadge = getRankBadge(index + 1);
                                    return (
                                        <Link
                                            key={legend.id}
                                            to={`/legend/${legend.id}`}
                                            className={styles.leaderboardItem}
                                        >
                                            <span className={`${styles.rank} ${rankBadge.class}`}>
                                                {rankBadge.icon}
                                            </span>
                                            <div className={styles.legendThumb}>
                                                {legend.cover_image_url ? (
                                                    <img src={legend.cover_image_url} alt={legend.title} />
                                                ) : (
                                                    <span className={styles.thumbPlaceholder}></span>
                                                )}
                                            </div>
                                            <div className={styles.legendInfo}>
                                                <span className={styles.legendTitle}>{legend.title}</span>
                                                <span className={styles.legendAuthor}>
                                                    {legend.rider?.nickname || '匿名骑士'}
                                                </span>
                                            </div>
                                            <div className={styles.legendSalutes}>
                                                <span className={styles.saluteIcon}></span>
                                                {legendApi.formatNumber(legend.salutes_count || 0)}
                                            </div>
                                        </Link>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* 顶尖骑士 */}
                    <div className={styles.leaderboardCard}>
                        <h3 className={styles.leaderboardTitle}>
                            <span></span> 顶尖骑士
                        </h3>
                        <div className={styles.leaderboardList}>
                            {topRiders.length === 0 ? (
                                <div className={styles.emptyList}>
                                    <p>暂无排行数据</p>
                                </div>
                            ) : (
                                topRiders.slice(0, 10).map((rider, index) => {
                                    const rankBadge = getRankBadge(index + 1);
                                    const levelInfo = getLevelInfo(rider.level);
                                    return (
                                        <Link
                                            key={rider.id}
                                            to={`/rider/${rider.id}`}
                                            className={styles.leaderboardItem}
                                        >
                                            <span className={`${styles.rank} ${rankBadge.class}`}>
                                                {rankBadge.icon}
                                            </span>
                                            <div className={styles.riderAvatar}>
                                                {rider.avatar_url ? (
                                                    <img src={rider.avatar_url} alt={rider.nickname} />
                                                ) : (
                                                    <span className={styles.avatarFallback}>
                                                        {rider.nickname?.charAt(0)?.toUpperCase() || 'R'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.riderInfo}>
                                                <span className={styles.riderName}>
                                                    {rider.nickname}
                                                    {rider.is_verified && <span className={styles.verified}></span>}
                                                </span>
                                                <span className={styles.riderLevel}>
                                                    {levelInfo?.icon} {levelInfo?.title}
                                                </span>
                                            </div>
                                            <div className={styles.riderSalutes}>
                                                <span className={styles.saluteIcon}></span>
                                                {legendApi.formatNumber(rider.total_salutes || 0)}
                                            </div>
                                        </Link>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* 浏览全部传奇 */}
            <section className={styles.ctaSection}>
                <div className={styles.ctaContent}>
                    <h2>探索更多传奇作品</h2>
                    <p>发现社区骑士们的精彩创作</p>
                    <Link to="/legends" className={styles.ctaBtn}>
                        浏览全部传奇
                    </Link>
                </div>
            </section>
        </div>
    );
}
